// @flow
/** @jsx jsx */

import { isNil, omitBy } from 'lodash';

import useFormState from '@zapier/payment/src/utils/useFormState';
import { PaymentFormButtons } from '@zapier/payment';
import { getValue } from '@zapier/payment/src/utils/formHelpers';
import { jsx } from '@emotion/core';
import { AsyncOverlay, MessageBar, Text } from '@zapier/common-components';
import {
  type PaymentDetailsBank,
  type PaymentSourceBackend,
  bankOwnershipTypes,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

import BankAccountFormFields from './BankAccountFormFields';
import CreditCardErrors from '../CreditCardForm/CreditCardErrors';
import useBraintreeBackend from './useBraintreeBackend';

type Props = {
  /** The payment backend to use */
  backend: PaymentSourceBackend,
  /** Key to connect with Braintree SDK */
  braintreeAPIKey: string,
  /** Error message to show */
  error?: string,
  /** Is currently saving */
  isEditingInFlight: boolean,
  /** Called when cancelling edit */
  onCancelEdit: () => void,
  /** Called if error is thrown */
  onError: (error: Error) => void,
  /** Called on form submit */
  onSave: (tokenDetails: any) => void,
  /** Currently saved bank details (if any) */
  savedAccount?: PaymentDetailsBank,
};

const mandateTextContainer = {
  marginTop: 20,
};

const BankAccountForm = (props: Props) => {
  const {
    state,
    updateErrors,
    checkIsValid,
    clearErrors,
    getFieldsErrors,
    startTokenization,
    stopTokenization,
    updateField,
  } = useFormState(paymentSourceMethods.bank, props.savedAccount);
  const braintreeClient = useBraintreeBackend(props.braintreeAPIKey);

  const hasServerError = !state.isDirty && props.error;
  const hasFormError = state.hasErrors;
  const submitText = 'Add Bank';
  const mandateText = `By clicking ["${submitText}"], I authorize Braintree, a service of PayPal, on behalf of Zapier (i) to verify my bank account information using bank information and consumer reports and (ii) to debit my bank account.`;
  const isBusy = props.isEditingInFlight || state.isTokenizing;

  const handleSave = async event => {
    event.preventDefault();

    const isValid = checkIsValid();

    if (!braintreeClient) {
      throw Error('Braintree client has not been initialized.');
    }

    const { fields } = state;
    const ownershipType = getValue('ownershipType', fields);
    const billingAddress = {
      streetAddress: getValue('streetAddress', fields),
      extendedAddress: getValue('extendedAddress', fields),
      locality: getValue('locality', fields),
      region: getValue('region', fields),
      postalCode: getValue('postalCode', fields),
    };

    const bankDetails = omitBy(
      {
        accountNumber: getValue('accountNumber', fields),
        routingNumber: getValue('routingNumber', fields),
        accountType: getValue('accountType', fields),
        ownershipType: getValue('ownershipType', fields),
        billingAddress,
        firstName:
          ownershipType === bankOwnershipTypes.personal
            ? getValue('firstName', fields)
            : null,
        lastName:
          ownershipType === bankOwnershipTypes.personal
            ? getValue('lastName', fields)
            : null,
        businessName:
          ownershipType === bankOwnershipTypes.business
            ? getValue('businessName', fields)
            : null,
      },
      isNil
    );

    if (isValid) {
      startTokenization();

      try {
        const token = await braintreeClient.tokenize({
          bankDetails,
          mandateText,
        });

        clearErrors();

        const result = {
          token: token.nonce,
          type: paymentSourceMethods.bank,
          paymentProcessor: props.backend,
          details: {
            ...token.details,
            address_city: billingAddress.locality,
            address_state: billingAddress.region,
            address_zip: billingAddress.postalCode,
            // Hardcoded to `US` - we only accept US bank accounts
            address_country: 'US',
          },
        };
        props.onSave(result);
      } catch (error) {
        clearErrors();
        props.onError(error);
      } finally {
        stopTokenization();
      }
    } else {
      updateErrors();
    }
  };

  return (
    <AsyncOverlay isVisible={isBusy}>
      <form
        // Used by E2E tests
        name="bank-form"
        onSubmit={handleSave}
      >
        {hasServerError ? (
          <MessageBar margin={{ bottom: 20 }} type="error">
            {props.error}
          </MessageBar>
        ) : (
          hasFormError && <CreditCardErrors errors={getFieldsErrors()} />
        )}

        <BankAccountFormFields
          fields={state.fields}
          onChangeField={updateField}
        />

        <div css={mandateTextContainer}>
          <Text type="fine">{mandateText}</Text>
        </div>

        <PaymentFormButtons
          buttonText={submitText}
          canSubmit={state.isDirty}
          hasCancelButton={true}
          isBusy={isBusy || !braintreeClient}
          onCancel={props.onCancelEdit}
        />
      </form>
    </AsyncOverlay>
  );
};

export default BankAccountForm;
